<template>
  <div class="mt-10">
    <div class="mb-4">
      <h3 class="fsize32 sm-fsize24 text-primary font-semibold mb-2">
        Treatment Options
      </h3>
      <p class="fsize14">
        As a caregiver, it can be overwhelming to navigate the various treatment
        options available for cancer. Each treatment option has its own set of
        benefits and side effects, and it's essential to understand them before
        making any decisions. In this blog, we will discuss the different
        treatment options available for cancer, the benefits and side effects of
        each option, and how they could impact the patient's quality of life.
      </p>
    </div>
    <div class="flex w-full gap-6 lg:pb-16 md:pb-12 pb-8">
      <div class="container">
        <div class="w-full flex gap-4">
          <div class="w-1/2">
            <img
              src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/cancer-page/Chemotherapy.jpg"
              alt="cancer"
              class="first_image_var1 object-cover"
            />
          </div>
          <div class="w-1/2">
            <h2 class="fsize20 font-bold text4F0A4B">Chemotherapy</h2>
            <p class="fsize14">
              Chemotherapy is a treatment that uses drugs to kill cancer cells
              in the body. These drugs can be given orally or intravenously, and
              they work by attacking rapidly dividing cells in the body,
              including cancer cells. The benefits of chemotherapy include:
            </p>
            <div class="mt-2">
              <ul class="list-disc ml-4">
                <li class="fsize14 pb-1">
                  <span class="fsize14 font-semibold"
                    >Shrinkage of tumors:</span
                  >
                  Chemotherapy can shrink tumors and help slow down the growth
                  of cancer.
                </li>
                <li class="fsize14 pb-1">
                  <span class="fsize14 font-semibold">Systemic treatment:</span>
                  Chemotherapy drugs can reach cancer cells throughout the body,
                  making it an effective treatment option for cancers that have
                  spread
                </li>
              </ul>
            </div>
            <p class="fsize14 mt-3">
              However, chemotherapy can also have significant side effects, such
              as:
            </p>
            <div class="mt-2">
              <ul class="list-disc ml-4">
                <li class="fsize14 pb-1">Fatigue</li>
                <li class="fsize14 pb-1">Nausea and vomiting</li>
                <li class="fsize14 pb-1">Hair loss</li>
                <li class="fsize14 pb-1">Infection</li>
                <li class="fsize14 pb-1">
                  Increased risk of bleeding and bruising
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="w-full flex gap-4 mt-4">
          <div class="w-1/2">
            <img
              src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/cancer-page/Radiation-Therapy.jpg"
              alt="cancer"
              class="first_image_var1 object-cover"
            />
          </div>
          <div class="w-1/2">
            <h2 class="fsize20 font-bold text4F0A4B">Radiation Therapy</h2>
            <p class="fsize14">
              Radiation therapy is a treatment that uses high-energy radiation
              to kill cancer cells in the body. Radiation can be delivered
              externally or internally, depending on the type and stage of
              cancer. The benefits of radiation therapy include:
            </p>
            <div class="mt-2">
              <ul class="list-disc ml-4">
                <li class="fsize14 pb-1">
                  <span class="fsize14 font-semibold"
                    >Localized treatment:
                  </span>
                  Radiation therapy targets specific areas of the body where
                  cancer cells are located.
                </li>
                <li class="fsize14 pb-1">
                  <span class="fsize14 font-semibold"> Pain relief:</span>
                  Radiation therapy can help relieve pain caused by cancer.
                </li>
              </ul>
            </div>
            <p class="fsize14 mt-3">
              However, radiation therapy can also have significant side effects,
              such as:
            </p>
            <div class="mt-2">
              <ul class="list-disc ml-4">
                <li class="fsize14 pb-1">Fatigue</li>
                <li class="fsize14 pb-1">Skin irritation</li>
                <li class="fsize14 pb-1">Nausea and vomiting</li>
                <li class="fsize14 pb-1">Diarrhea</li>
                <li class="fsize14 pb-1">Increased risk of infection</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="w-full flex gap-4 mt-4">
          <div class="w-1/2">
            <img
              src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/cancer-page/Surgery.jpg"
              alt="cancer"
              class="first_image_var1 object-cover"
            />
          </div>
          <div class="w-1/2">
            <h2 class="fsize20 font-bold text4F0A4B">Surgery</h2>
            <p class="fsize14">
              Surgery is a treatment that involves the removal of cancerous
              tissue from the body. The benefits of surgery include:
            </p>
            <div class="mt-2">
              <ul class="list-disc ml-4">
                <li class="fsize14 pb-1">
                  <span class="fsize14 font-semibold">Removal of cancer: </span>
                  Surgery can remove all or part of a tumor, potentially curing
                  cancer.
                </li>
                <li class="fsize14 pb-1">
                  <span class="fsize14 font-semibold">Diagnosis:</span>
                  Surgery can provide a definitive diagnosis of cancer and help
                  determine its stage.
                </li>
              </ul>
            </div>
            <p class="fsize14 mt-3">
              However, surgery can also have significant side effects, such as:
            </p>
            <div class="mt-2">
              <ul class="list-disc ml-4">
                <li class="fsize14 pb-1">Pain and discomfort</li>
                <li class="fsize14 pb-1">Infection</li>
                <li class="fsize14 pb-1">Bleeding</li>
                <li class="fsize14 pb-1">Scarring</li>
                <li class="fsize14 pb-1">
                  Risk of complications, such as blood clots and pneumonia
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <!-- <div class="w-1/6 border-left py-4 pl-4">
        <div class="border-bottom">
          <h2 class="fsize16 font-semibold pb-1">Our Latest Blogs</h2>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            5 Essential Tips for Balancing Caregiving and Self-Care
          </p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            Navigating the Emotional Challenges of Being a...
          </p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            How to Create a Support Network for Caregivers
          </p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            Understanding the Legal Rights of Family Caregivers
          </p>
          <p class="fsize13 lh16px pb-3 clr-0087d4">
            10 Signs of Caregiver Burnout and How to Avoid It
          </p>
        </div>
        <div class="border-bottom py-3">
          <h2 class="fsize16 font-semibold pb-1">Latest Research</h2>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            The Mental Health Impact of Caregiving on Family Members
          </p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            Emerging Trends in Palliative Care: What Caregivers Need ..
          </p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            he Science Behind Compassion Fatigue: What...
          </p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            Latest Research on the Emotional Toll of End-of-Life..
          </p>
          <p class="fsize13 lh16px pb-3 clr-0087d4">
            Emerging Trends in Palliative Care: What Caregivers...
          </p>
        </div>
        <div class="border-bottom py-3">
          <h2 class="fsize16 font-semibold pb-1">Explore Resources</h2>
          <p class="fsize13 lh16px pb-2 clr-0087d4">Emotional First Aid</p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">Caregiver Coach Program</p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">Mental health Awareness</p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">Medical Terminology</p>
          <p class="fsize13 lh16px pb-3 clr-0087d4">Caring at Caregiver sath</p>
        </div>
        <div class="border-bottom py-3">
          <h2 class="fsize16 font-semibold pb-1">
            Helpful Links and Collaborations
          </h2>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            Duis volutpat pulvinar nisi
          </p>
          <p class="fsize13 lh16px pb-2 clr-0087d4">
            et rutrum nisl rhoncus nec
          </p>
        </div>
      </div>//// -->
      <!-- <div class="border-left lg:w-2/12 md:w-2/12 w-full lg:mt-0 md:mt-0 mt-4">
        <SideBlogs />
      </div> -->
    </div>
    <HealthcareProvider />
  </div>
</template>

<script>
import HealthcareProvider from "./components/HealthcareProvider.vue";
import SideBlogs from "./components/SideBlogs.vue";

export default {
  components: {
    HealthcareProvider,
    SideBlogs,
  },
};
</script>

<style>
.card_guidance_img {
  box-shadow: 4px 4px 14px 0px #0000003d;

  border-radius: 0px 0 80px 0;
}
.first_image_var1 {
  height: 340px;
  width: 100%;
  border-radius: 80px 0 0 0;
}

.second_img {
  height: 180px;
  width: 180px;
  border-radius: 0 80px 0 80px;
}

.third_img {
  height: 180px;
  width: 180px;
  border-radius: 80px 0 80px 0;
}

.sign_img {
  width: 220px;
  height: 220px;
  border-radius: 50% 50% 0 0;
}
</style>
